import React, { useRef, useLayoutEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Heart, Home, Users, Flower2, Sun, TreePine, Clock, Utensils, Pill, Wrench, Shield, Car, Bed, Film, Sparkles, Armchair } from 'lucide-react';
import './SeniorLivingSection.css';

const SeniorLivingSection = () => {
    const containerRef = useRef(null);

    useLayoutEffect(() => {
        gsap.registerPlugin(ScrollTrigger);

        const ctx = gsap.context(() => {
            // Leaf 1 - Moves up slightly faster
            gsap.to('.leaf-1', {
                y: -120,
                ease: 'none',
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: 1
                }
            });

            // Leaf 2 - Moves down slightly
            gsap.to('.leaf-2', {
                y: 80,
                ease: 'none',
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: 1.5
                }
            });

            // Flower 1 - Moves up slower
            gsap.to('.flower-1', {
                y: -60,
                ease: 'none',
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: 1.2
                }
            });

            // Flower 2 - Moves up fast
            gsap.to('.flower-2', {
                y: -150,
                ease: 'none',
                scrollTrigger: {
                    trigger: containerRef.current,
                    start: 'top bottom',
                    end: 'bottom top',
                    scrub: 0.8
                }
            });
        }, containerRef);

        return () => ctx.revert();
    }, []);

    return (
        <section id="property" className="senior-living-section" ref={containerRef}>
            {/* Decorative nature elements */}
            <div className="nature-decoration leaf-1">
                <div className="float-content">🍃</div>
            </div>
            <div className="nature-decoration leaf-2">
                <div className="float-content">🍃</div>
            </div>
            <div className="nature-decoration flower-1">
                <div className="float-content">🌸</div>
            </div>
            <div className="nature-decoration flower-2">
                <div className="float-content">🌺</div>
            </div>

            <div className="senior-living-container">
                {/* Hero Content */}
                {/* Hero Content - Amenities & Features */}
                <div className="senior-hero-content">
                    <div className="senior-badge">
                        <Heart size={16} />
                        <span>Hollywood Royale Garden</span>
                    </div>

                    <h2 className="senior-title">
                        Assisted Living<br />
                        <span className="highlight">Amenities & Features</span>
                    </h2>

                    <p className="senior-description">
                        Experience the best in assisted living with our comprehensive range of amenities
                        designed for comfort, engagement, and well-being.
                    </p>

                    <div className="senior-features-list">
                        <div className="senior-feature-item">
                            <div className="feature-bullet">
                                <Shield size={24} />
                            </div>
                            <div className="feature-text-content">
                                <h3 className="feature-inline-title">24/7 Care & Security</h3>
                                <span className="feature-inline-desc"> - Peace of mind with round-the-clock staffing and safety</span>
                            </div>
                        </div>

                        <div className="senior-feature-item">
                            <div className="feature-bullet">
                                <Home size={24} />
                            </div>
                            <div className="feature-text-content">
                                <h3 className="feature-inline-title">Hollywood Sign View</h3>
                                <span className="feature-inline-desc"> - Elegant living room with iconic views</span>
                            </div>
                        </div>

                        <div className="senior-feature-item">
                            <div className="feature-bullet">
                                <Bed size={24} />
                            </div>
                            <div className="feature-text-content">
                                <h3 className="feature-inline-title">Spacious Bedrooms</h3>
                                <span className="feature-inline-desc"> - Master bedrooms designed for comfort and privacy</span>
                            </div>
                        </div>

                        <div className="senior-feature-item">
                            <div className="feature-bullet">
                                <Utensils size={24} />
                            </div>
                            <div className="feature-text-content">
                                <h3 className="feature-inline-title">Elegant Dining</h3>
                                <span className="feature-inline-desc"> - Enjoy delicious meals in our sophisticated dining room</span>
                            </div>
                        </div>

                        <div className="senior-feature-item">
                            <div className="feature-bullet">
                                <TreePine size={24} />
                            </div>
                            <div className="feature-text-content">
                                <h3 className="feature-inline-title">Open-Air Courtyard</h3>
                                <span className="feature-inline-desc"> - Beautiful outdoor spaces for relaxation and fresh air</span>
                            </div>
                        </div>

                        <div className="senior-feature-item">
                            <div className="feature-bullet">
                                <Film size={24} />
                            </div>
                            <div className="feature-text-content">
                                <h3 className="feature-inline-title">Theater & Activity Hub</h3>
                                <span className="feature-inline-desc"> - Dedicated spaces for entertainment and social activities</span>
                            </div>
                        </div>

                        <div className="senior-feature-item">
                            <div className="feature-bullet">
                                <Sparkles size={24} />
                            </div>
                            <div className="feature-text-content">
                                <h3 className="feature-inline-title">Wellness & Salon</h3>
                                <span className="feature-inline-desc"> - On-site wellness center and beauty salon services</span>
                            </div>
                        </div>

                        <div className="senior-feature-item">
                            <div className="feature-bullet">
                                <Armchair size={24} />
                            </div>
                            <div className="feature-text-content">
                                <h3 className="feature-inline-title">Family Lounge</h3>
                                <span className="feature-inline-desc"> - Comfortable gathering spaces for visiting with loved ones</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Comprehensive Care Services Section - Modern Design */}
                <div id="amenities" className="care-services-section">
                    <div className="care-services-header">
                        <div className="header-badge">
                            <Heart size={18} className="badge-icon" />
                            <span>Excellence in Care</span>
                        </div>
                        <h3 className="care-services-title">
                            Comprehensive Care Services
                        </h3>
                        <p className="care-services-subtitle">
                            Dedicated support designed around your unique needs and preferences
                        </p>
                        <div className="title-underline">
                            <span className="underline-dot"></span>
                            <span className="underline-line"></span>
                            <span className="underline-dot"></span>
                        </div>
                    </div>

                    <div className="care-services-list">
                        <div className="senior-feature-item">
                            <div className="feature-bullet">
                                <Clock size={24} />
                            </div>
                            <div className="feature-text-content">
                                <h3 className="feature-inline-title">Personalized Care</h3>
                                <span className="feature-inline-desc"> - 24 hour personalized care planning to meet the unique needs of each resident</span>
                            </div>
                        </div>

                        <div className="senior-feature-item">
                            <div className="feature-bullet">
                                <Utensils size={24} />
                            </div>
                            <div className="feature-text-content">
                                <h3 className="feature-inline-title">Nutritious Meals</h3>
                                <span className="feature-inline-desc"> - Homestyle meals and snacks designed to support nutritional needs and cultural preferences</span>
                            </div>
                        </div>

                        <div className="senior-feature-item">
                            <div className="feature-bullet">
                                <Pill size={24} />
                            </div>
                            <div className="feature-text-content">
                                <h3 className="feature-inline-title">Medication Management</h3>
                                <span className="feature-inline-desc"> - Centrally stored and dispensed medication management, promoting health and wellbeing</span>
                            </div>
                        </div>

                        <div className="senior-feature-item">
                            <div className="feature-bullet">
                                <Wrench size={24} />
                            </div>
                            <div className="feature-text-content">
                                <h3 className="feature-inline-title">Maintenance & Housekeeping</h3>
                                <span className="feature-inline-desc"> - Professional housekeeping and maintenance services to keep your living space comfortable</span>
                            </div>
                        </div>

                        <div className="senior-feature-item">
                            <div className="feature-bullet">
                                <Car size={24} />
                            </div>
                            <div className="feature-text-content">
                                <h3 className="feature-inline-title">Transportation</h3>
                                <span className="feature-inline-desc"> - We will assist with coordinating transportation to doctor appointments and other health care needs</span>
                            </div>
                        </div>

                        <div className="senior-feature-item">
                            <div className="feature-bullet">
                                <Shield size={24} />
                            </div>
                            <div className="feature-text-content">
                                <h3 className="feature-inline-title">Safety & Security</h3>
                                <span className="feature-inline-desc"> - In addition to 24/7 staffing, we have technology and systems that promote safety and security</span>
                            </div>
                        </div>
                    </div>

                    {/* Stats Banner */}
                    <div className="care-stats-banner">
                        <div className="stat-item">
                            <div className="stat-number">24/7</div>
                            <div className="stat-label">Care Available</div>
                        </div>
                        <div className="stat-divider"></div>
                        <div className="stat-item">
                            <div className="stat-number">100%</div>
                            <div className="stat-label">Personalized Plans</div>
                        </div>
                        <div className="stat-divider"></div>
                        <div className="stat-item">
                            <div className="stat-number">Expert</div>
                            <div className="stat-label">Professional Staff</div>
                        </div>
                    </div>
                </div>



            </div>
        </section>
    );
};

export default SeniorLivingSection;
